import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Eye, ImagePlus, LayoutTemplate, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui-kit";
import { HeroCfg, HeroSlide, HeroView } from "@/components/Hero";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";

const SlideRow = ({ s, n, total, set, move, remove, canEdit }: { s: HeroSlide; n: number; total: number; set: (s: HeroSlide) => void; move: (d: number) => void; remove: () => void; canEdit: boolean }) => {
  const { t } = usePrefs();
  const [busy, setBusy] = useState(false);
  const upload = async (f?: File) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try { const { data } = await api.post("/admin/uploads", fd); set({ ...s, image: data.url }); } catch (e) { toast.error(errorText(e, t)); } finally { setBusy(false); }
  };
  return (
    <li data-testid={`hero-slide-${n}`} className="rounded-2xl border p-2.5 flex items-center gap-3">
      <label className={`relative w-24 shrink-0 aspect-[3/2] rounded-xl overflow-hidden bg-muted grid place-items-center ${canEdit ? "cursor-pointer" : ""}`}>
        {s.image ? <img src={s.image} alt="" className="absolute inset-0 h-full w-full object-cover" /> : <ImagePlus className="h-5 w-5 text-muted-foreground" />}
        {busy && <span className="absolute inset-0 bg-black/50 grid place-items-center text-white text-xs">…</span>}
        {canEdit && <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="sr-only" data-testid={`hero-image-${n}`} onChange={(e) => upload(e.target.files?.[0])} />}
      </label>
      <div className="flex-1 min-w-0 grid gap-1.5">
        <Input value={s.label_ar} disabled={!canEdit} maxLength={30} placeholder={t("hs.labelAr")} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ ...s, label_ar: e.target.value })} data-testid={`hero-label_ar-${n}`} className="h-9 rounded-xl text-sm" />
        <Input value={s.label_en} disabled={!canEdit} maxLength={30} dir="ltr" placeholder={t("hs.labelEn")} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ ...s, label_en: e.target.value })} data-testid={`hero-label_en-${n}`} className="h-9 rounded-xl text-sm" />
      </div>
      {canEdit && (
        <div className="grid gap-1">
          <button disabled={n === 0} onClick={() => move(-1)} aria-label={t("onb.up")} data-testid={`hero-up-${n}`} className="h-7 w-7 grid place-items-center rounded-full border disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
          <button disabled={n === total - 1} onClick={() => move(1)} aria-label={t("onb.down")} data-testid={`hero-down-${n}`} className="h-7 w-7 grid place-items-center rounded-full border disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
          <button disabled={total === 1} onClick={remove} aria-label={t("hs.delete")} data-testid={`hero-delete-${n}`} className="h-7 w-7 grid place-items-center rounded-full border text-destructive disabled:opacity-30"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </li>
  );
};

export const HeroSettings = ({ canEdit }: { canEdit: boolean }) => {
  const { t } = usePrefs();
  const [f, setF] = useState<HeroCfg | null>(null);
  const [preview, setPreview] = useState(false);
  useEffect(() => { api.get("/hero").then((r) => setF(r.data)).catch(() => {}); }, []);
  if (!f) return null;
  const set = (k: keyof HeroCfg, v: unknown) => setF({ ...f, [k]: v });
  const slides = f.slides;
  const move = (i: number, d: number) => { const c = [...slides]; [c[i], c[i + d]] = [c[i + d], c[i]]; set("slides", c); };
  const save = async () => {
    const empty = slides.findIndex((s) => !s.image);
    if (empty >= 0) return toast.error(t("hs.needImage").replace("{n}", String(empty + 1)));
    try { const { data } = await api.put("/admin/hero", f); setF(data); toast.success(t("hs.saved")); } catch (e) { toast.error(errorText(e, t)); }
  };
  const text = (k: "title_ar" | "title_en" | "subtitle_ar" | "subtitle_en", label: string, ph: string, area = false) => (
    <label className="text-xs font-bold space-y-1 block">{label}
      {area ? <textarea value={f[k]} disabled={!canEdit} maxLength={200} rows={2} dir={k.endsWith("en") ? "ltr" : "rtl"} placeholder={ph} onChange={(e) => set(k, e.target.value)} data-testid={`hero-${k}`} className="w-full rounded-2xl border bg-background px-3 py-2 text-sm font-normal" />
        : <Input value={f[k]} disabled={!canEdit} maxLength={60} dir={k.endsWith("en") ? "ltr" : "rtl"} placeholder={ph} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set(k, e.target.value)} data-testid={`hero-${k}`} className="h-10 rounded-2xl" />}
    </label>
  );
  return (
    <div data-testid="hero-settings" className="rounded-3xl border bg-card p-5 space-y-4">
      <div className="flex gap-3">
        <LayoutTemplate className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        <div><p className="font-bold">{t("hs.title")}</p><p className="text-sm text-muted-foreground">{t("hs.note")}</p></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {text("title_ar", t("hs.titleAr"), t("hero.title"))}
        {text("subtitle_ar", t("hs.subAr"), t("hero.subtitle"), true)}
      </div>
      <details className="text-xs"><summary className="cursor-pointer font-bold text-muted-foreground">{t("onb.english")}</summary>
        <div className="mt-2 grid sm:grid-cols-2 gap-3">{text("title_en", t("hs.titleEn"), "Where to today?")}{text("subtitle_en", t("hs.subEn"), "", true)}</div>
      </details>
      <div>
        <p className="text-xs font-bold mb-2">{t("hs.images")}</p>
        <ul className="space-y-2">
          {slides.map((s, i) => <SlideRow key={s.id} s={s} n={i} total={slides.length} canEdit={canEdit} set={(x) => set("slides", slides.map((y, k) => (k === i ? x : y)))} move={(d) => move(i, d)} remove={() => set("slides", slides.filter((_, k) => k !== i))} />)}
        </ul>
      </div>
      <label className="text-xs font-bold flex items-center gap-2">{t("hs.interval")}
        <select value={f.interval} disabled={!canEdit} onChange={(e) => set("interval", Number(e.target.value))} data-testid="hero-interval" className="h-9 rounded-full border bg-background px-3 text-sm">
          {[3, 4, 5, 6, 8, 10, 15].map((x) => <option key={x} value={x}>{t("hs.seconds").replace("{n}", String(x))}</option>)}
        </select>
      </label>
      <div className="flex flex-wrap gap-2">
        {canEdit && slides.length < 8 && <button onClick={() => set("slides", [...slides, { id: `h${Date.now().toString(36)}`, image: "", label_ar: "", label_en: "" }])} data-testid="hero-add" className="h-10 px-4 rounded-full border text-sm font-bold inline-flex items-center gap-1.5"><Plus className="h-4 w-4" />{t("hs.add")}</button>}
        <button onClick={() => setPreview(!preview)} data-testid="hero-preview" className="h-10 px-4 rounded-full border text-sm font-bold inline-flex items-center gap-1.5"><Eye className="h-4 w-4" />{t("onb.preview")}</button>
        {canEdit && <button onClick={save} data-testid="hero-save" className="h-10 px-5 rounded-full bg-primary text-primary-foreground text-sm font-bold">{t("common.save")}</button>}
      </div>
      {preview && <div data-testid="hero-preview-box"><HeroView cfg={{ ...f, slides: slides.filter((s) => s.image) }} /></div>}
    </div>
  );
};
